import { Grid, SelectChangeEvent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocalStorage } from 'usehooks-ts';

import { Select } from '@/components/Form/Select';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';
import { selectTerritories } from '@/redux/countries';

import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';

type BranchSelectProps = {
  disabled: boolean;
};

const LS_BRANCH_KEY = 'onboarding_selected_branch';

export const BranchSelect = ({ disabled }: BranchSelectProps) => {
  const { t } = useTranslate();
  const { excludedBranchCodes } = useAppSelector(selectConfigsState);
  const { territoryByCountryCode } = useAppSelector(selectTerritories);
  const [branchOptions, setBranchOptions] = useState<Option[]>([]);
  const [savedBranch, setSavedBranch] = useLocalStorage(LS_BRANCH_KEY, '');
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const countryCode = watch('countryCode');

  useEffect(() => {
    const newBranchOptions: Option[] =
      territoryByCountryCode[countryCode]?.branches
        ?.filter(branch => !excludedBranchCodes.includes(branch.branchNo))
        .map(branch => ({
          value: branch.id,
          text: branch.branchName,
        })) || [];

    setBranchOptions(newBranchOptions);

    // Load saved branch if it exists and is valid for the selected country
    if (newBranchOptions.length > 0) {
      const isValidBranch = newBranchOptions.some(
        option => option.value === savedBranch,
      );
      setValue('branchId', isValidBranch ? savedBranch : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, excludedBranchCodes, setValue, territoryByCountryCode]);

  const handleAfterBranchSelect = (e: SelectChangeEvent<unknown>) => {
    const branchId = e.target.value as string;
    setSavedBranch(branchId);
  };
  return (
    <Grid item xs={12}>
      <Select
        options={branchOptions}
        control={control}
        name="branchId"
        label={t('countrySelect.form.branch.label')}
        error={!!errors.branchId}
        helperText={errors.branchId?.message as string}
        rules={{
          required: t('countrySelect.form.branch.error.required'),
        }}
        fullWidth
        disabled={disabled}
        inputProps={{
          ...getDataTestId('branch-select-input'),
        }}
        onSelectChange={handleAfterBranchSelect}
      />
    </Grid>
  );
};
